// apps/share-web/src/offline/open-store.ts
//
// Как приложение ПОЛУЧАЕТ хранилище офлайна и как оно его СТИРАЕТ. Один приём
// на все маршруты: библиотека, страница слов, меню аккаунта в шапке. Второй
// экземпляр этого приёма разошёлся бы с первым молча — например, взял бы
// личность из другого места и открыл бы человеку чужой кэш.
//
// ЛИЧНОСТЬ ИЗ localStorage, а не из `/api/me`, потому что хранилище нужно ДО
// сети: ключ кэша начинается с userId, и без него нечего показать в первую
// секунду — ровно ту, ради которой волна делается. Кладёт значение ветка
// /library после успешного `/api/me`; это непрозрачный идентификатор в
// собственном браузере человека, не секрет, и ошибка в нём ничего не
// открывает — кэш всё равно читается только по совпадению ключа.

import { createOfflineStore, type CachedMe, type OfflineStore } from './offline-store.js';
import { idbBackend } from './idb-backend.js';
import { memoryBackend } from './kv-backend.js';
import { applySyncAction } from './apply-sync.js';

export const OFFLINE_UID_KEY = 'bw.lib.uid';

/** `null` — на этом телефоне ещё не входили или хранилище браузера
 *  заблокировано политикой (приватный режим). */
export function readOfflineUserId(): string | null {
  try { return localStorage.getItem(OFFLINE_UID_KEY); } catch { return null; }
}

/**
 * Хранилище офлайна этого человека. IndexedDB там, где он есть; память там,
 * где его нет (приватный режим, старый WebView) — она живёт до перезагрузки и
 * молча исчезает, что честнее наполовину отключённой функции. `null` — когда
 * вошедший неизвестен: кэша у него нет по построению.
 */
export function openOfflineStore(uid: string | null = readOfflineUserId()): OfflineStore | null {
  return uid ? createOfflineStore(idbBackend() ?? memoryBackend(), uid) : null;
}

/**
 * Кэш вошедшего для офлайн-показа шапки (дефект 2 живой приёмки волны G,
 * ui/app-header.ts). Тот же приём, что у остального модуля: ОДНА функция на
 * все маршруты (шапка стоит и в full-bleed видах, и в плеере — main.ts),
 * второй экземпляр которой разошёлся бы с первым молча. `null` — хранилища
 * нет (свежий телефон/приватный режим) или записи ещё не было; отказ
 * хранилища глотается здесь же — офлайн-шапка усиление, не несущая часть,
 * и падать на нём нечем, кроме как в «Sign in» вошедшему.
 */
export async function loadCachedMe(uid: string | null = readOfflineUserId()): Promise<CachedMe | null> {
  const store = openOfflineStore(uid);
  if (!store) return null;
  return store.loadMe().catch(() => null);
}

/**
 * Стереть офлайн-данные этого человека.
 *
 * Вместе с выходом из аккаунта (он зовёт эту же функцию — ui/app-header.ts,
 * и ДО `signOut`) это ЕДИНСТВЕННЫЙ путь стирания во всём продукте: после
 * ревью качества 401 кэш не трогает, потому что один отказ прокси посреди
 * служения не имеет права обнулить библиотеку.
 *
 * Исполняется тем же `applySyncAction`, что и остальные решения о судьбе
 * кэша, — второго пути к `clearUser` в приложении нет. Аргументы со
 * значениями по умолчанию: в проде их не передаёт никто, в тестах подставляют
 * оба.
 */
export async function eraseOfflineData(
  store: OfflineStore | null = openOfflineStore(),
  userId: string | null = readOfflineUserId(),
): Promise<void> {
  if (!store || !userId) return;
  await applySyncAction({ store, userId, action: { kind: 'wipe' } });
}
